import type { ProjectileDirection } from "@ninja/contracts";

export interface SpriteAppearance {
  id: string;
  resourceId: number;
  role: "ninja" | "temporary_monster";
  sourceDirectory: "outfits";
  sourceGroup: 0 | 1 | null;
  sourceType: 0 | 1 | null;
  sourceFrames: number[];
  sourceDirections: number[];
  hasFrameSuffix: boolean;
  sourceFrameSize: 64;
  anchor: { x: number; y: number };
  scale: number;
}

function appearance(
  id: string,
  resourceId: number,
  role: SpriteAppearance["role"],
  sourceGroup: 0 | 1 | null,
  sourceType: 0 | 1 | null,
  sourceFrames: number[],
  sourceDirections: number[] = [0],
): SpriteAppearance {
  return {
    id,
    resourceId,
    role,
    sourceDirectory: "outfits",
    sourceGroup,
    sourceType,
    sourceFrames,
    sourceDirections,
    hasFrameSuffix: sourceFrames.length > 1,
    sourceFrameSize: 64,
    anchor: { x: 0.5, y: 0.82 },
    // Frames include substantial transparent padding; these values fit the
    // visible character pixels into one 32px world tile.
    scale: role === "ninja" ? 4 / 3 : 1.25,
  };
}

const NINJA_OUTFIT_RESOURCE_IDS = [
  ...Array.from({ length: 36 }, (_, index) => index + 1),
  ...Array.from({ length: 35 }, (_, index) => index + 301),
];
const NINJA_DIRECTIONS = [0, 1, 2, 3];
const NINJA_FRAMES = Array.from({ length: 8 }, (_, index) => index);
const FRONTIER_MONSTER_APPEARANCES = [
  { id: "monster-swordsman-bandit", resourceId: 2129 },
  { id: "monster-renegade-marksman", resourceId: 2131 },
  { id: "monster-renegade-trapper", resourceId: 2135 },
  { id: "monster-renegade-support-ninja", resourceId: 2149 },
  { id: "monster-renegade-captain", resourceId: 2139 },
  { id: "monster-frontier-deserter-jonin", resourceId: 2140 },
].map(({ id, resourceId }) => [
  id,
  appearance(
    id,
    resourceId,
    "temporary_monster",
    1,
    1,
    NINJA_FRAMES,
    NINJA_DIRECTIONS,
  ),
]);
const BLACK_FORGE_MONSTER_APPEARANCES = [
  { id: "monster-renegade-miner", resourceId: 2131 },
  { id: "monster-forge-saboteur", resourceId: 2132 },
  { id: "monster-ash-shinobi", resourceId: 2127 },
  { id: "monster-slag-golem", resourceId: 2093 },
  { id: "monster-black-forge-overseer", resourceId: 2169 },
  { id: "monster-black-forge-master", resourceId: 2184 },
].map(({ id, resourceId }) => [
  id,
  appearance(
    id,
    resourceId,
    "temporary_monster",
    1,
    1,
    NINJA_FRAMES,
    NINJA_DIRECTIONS,
  ),
]);

export const NINJA_APPEARANCE_IDS = NINJA_OUTFIT_RESOURCE_IDS.map(
  (resourceId) => `ninja-${resourceId}`,
);

const RANGE_NINJA_APPEARANCES = Object.fromEntries(
  NINJA_OUTFIT_RESOURCE_IDS.map((resourceId) => {
    const id = `ninja-${resourceId}`;
    return [
      id,
      appearance(id, resourceId, "ninja", 1, 1, NINJA_FRAMES, NINJA_DIRECTIONS),
    ];
  }),
);

export const APPEARANCES: Record<string, SpriteAppearance> = {
  // Retain old IDs so ninjas already saved in development databases keep rendering.
  "ninja-ember": appearance(
    "ninja-ember",
    2022,
    "ninja",
    1,
    1,
    [0, 1, 2, 3, 4, 5, 6, 7],
  ),
  "ninja-cobalt": appearance(
    "ninja-cobalt",
    1202,
    "ninja",
    null,
    null,
    [0, 1, 2, 3],
  ),
  "ninja-rose": appearance(
    "ninja-rose",
    1203,
    "ninja",
    null,
    null,
    [0, 1, 2, 3, 4, 5, 6, 7, 8, 9],
  ),
  "ninja-snow": appearance(
    "ninja-snow",
    1204,
    "ninja",
    null,
    null,
    [0, 1, 2, 3],
  ),
  "ninja-ochre": appearance(
    "ninja-ochre",
    1205,
    "ninja",
    null,
    null,
    [0, 1, 2, 3],
  ),
  ...RANGE_NINJA_APPEARANCES,
  "wolf-temporary": appearance(
    "wolf-temporary",
    2025,
    "temporary_monster",
    1,
    1,
    NINJA_FRAMES,
    NINJA_DIRECTIONS,
  ),
  "alpha-wolf-temporary": appearance(
    "alpha-wolf-temporary",
    2059,
    "temporary_monster",
    1,
    1,
    [0, 1],
    NINJA_DIRECTIONS,
  ),
  "ancestral-pack-wolf-temporary": appearance(
    "ancestral-pack-wolf-temporary",
    1400,
    "temporary_monster",
    1,
    1,
    [0, 1, 2, 3],
    NINJA_DIRECTIONS,
  ),
  "brown-bear-temporary": appearance(
    "brown-bear-temporary",
    2088,
    "temporary_monster",
    1,
    1,
    [0, 1, 2, 3, 4, 5, 6, 7],
    NINJA_DIRECTIONS,
  ),
  ...Object.fromEntries(FRONTIER_MONSTER_APPEARANCES),
  ...Object.fromEntries(BLACK_FORGE_MONSTER_APPEARANCES),
};

export function appearanceByResourceId(
  resourceId: number,
): SpriteAppearance | undefined {
  return Object.values(APPEARANCES).find(
    (entry) => entry.resourceId === resourceId,
  );
}

export function scaleForTile(
  entry: SpriteAppearance,
  tileSize: number,
): number {
  if (!Number.isFinite(tileSize) || tileSize <= 0)
    throw new RangeError("Tile size must be a positive number");
  return (tileSize / entry.sourceFrameSize) * entry.scale;
}

export const TERRAIN_ASSETS = {
  forestGrass: {
    resourceId: 106,
    path: "assets/items/ground/item_106_x0_y0.png",
    width: 32,
    height: 32,
  },
  detailGrass: {
    resourceId: 293,
    path: "assets/items/ground/item_293_x0_y0.png",
    width: 32,
    height: 32,
  },
  soil: {
    resourceId: 101,
    path: "assets/items/ground/item_101_x0_y0.png",
    width: 32,
    height: 32,
  },
  canyonPath: {
    resourceId: 103,
    path: "assets/items/ground/item_103_x0_y0.png",
    width: 32,
    height: 32,
  },
} as const;

export const MAP_OBJECTS = {
  foliage64: {
    resourceId: 10000,
    path: "assets/items/blocking_objects/item_10000.png",
    width: 64,
    height: 64,
  },
  groundObject32: {
    resourceId: 10007,
    path: "assets/items/blocking_objects/item_10007.png",
    width: 32,
    height: 32,
  },
} as const;

/** Curated icons used by owned equipment and materials. */
export const ITEM_ICONS = {
  armor: { path: "assets/items/equipaments/armor.webp", width: 64, height: 64 },
  boots: { path: "assets/items/equipaments/boots.webp", width: 64, height: 64 },
  bracelet: {
    path: "assets/items/equipaments/bracelet.webp",
    width: 64,
    height: 64,
  },
  earring: {
    path: "assets/items/equipaments/earring.webp",
    width: 64,
    height: 64,
  },
  glove: {
    path: "assets/items/equipaments/gloves.webp",
    width: 64,
    height: 64,
  },
  helmet: {
    path: "assets/items/equipaments/helmet.webp",
    width: 64,
    height: 64,
  },
  legs: { path: "assets/items/equipaments/legs.webp", width: 64, height: 64 },
  necklace: {
    path: "assets/items/equipaments/necklace.webp",
    width: 64,
    height: 64,
  },
  ring: { path: "assets/items/equipaments/ring.webp", width: 64, height: 64 },
  secondary_weapon: {
    path: "assets/items/equipaments/secondary-weapon.webp",
    width: 64,
    height: 64,
  },
  weapon: {
    path: "assets/items/equipaments/weapon.webp",
    width: 64,
    height: 64,
  },
  wolf_fang: {
    path: "assets/items/creature_items/wolf_fang.webp",
    width: 64,
    height: 64,
  },
  bear_claw: {
    path: "assets/items/creature_items/bear_claw.webp",
    width: 64,
    height: 64,
  },
  black_forge_crest: {
    path: "assets/items/creature_items/black_forge_crest.webp",
    width: 64,
    height: 64,
  },
  black_tool_fragment: {
    path: "assets/items/creature_items/black_tool_fragment.webp",
    width: 64,
    height: 64,
  },
  charred_fuse: {
    path: "assets/items/creature_items/charred_fuse.webp",
    width: 64,
    height: 64,
  },
  overseer_insignia: {
    path: "assets/items/creature_items/overseer_insignia.webp",
    width: 64,
    height: 64,
  },
  sealed_ash: {
    path: "assets/items/creature_items/sealed_ash.webp",
    width: 64,
    height: 64,
  },
  slag_core: {
    path: "assets/items/creature_items/slag_core.webp",
    width: 64,
    height: 64,
  },
  academy_summoning_scroll_uncommon: {
    path: "assets/items/scrolls/academy/summoning-scroll-uncommon.webp",
    width: 64,
    height: 64,
  },
  academy_summoning_scroll_rare: {
    path: "assets/items/scrolls/academy/summoning-scroll-rare.webp",
    width: 64,
    height: 64,
  },
  academy_summoning_scroll_epic: {
    path: "assets/items/scrolls/academy/summoning-scroll-epic.webp",
    width: 64,
    height: 64,
  },
  academy_summoning_scroll_legendary: {
    path: "assets/items/scrolls/academy/summoning-scroll-legendary.webp",
    width: 64,
    height: 64,
  },
  // Reuse the linked technique illustration until dedicated scroll artwork exists.
  technique_scroll_impacto_frontal: {
    path: "assets/icons/jutsus/impacto_frontal.webp",
    width: 64,
    height: 64,
  },
  technique_scroll_lamina_de_vento: {
    path: "assets/icons/jutsus/lamina_de_vento.webp",
    width: 64,
    height: 64,
  },
  technique_scroll_selo_explosivo: {
    path: "assets/icons/jutsus/selo_explosivo.webp",
    width: 64,
    height: 64,
  },
  technique_scroll_palma_restauradora: {
    path: "assets/icons/jutsus/palma_restauradora.webp",
    width: 64,
    height: 64,
  },
  technique_scroll_ilusao_do_medo: {
    path: "assets/icons/jutsus/ilusao_do_medo.webp",
    width: 64,
    height: 64,
  },
  technique_scroll_queda_do_trovao: {
    path: "assets/icons/jutsus/queda_do_trovao.webp",
    width: 64,
    height: 64,
  },
  technique_scroll_selo_de_proximidade: {
    path: "assets/icons/jutsus/selo_de_proximidade.webp",
    width: 64,
    height: 64,
  },
  technique_scroll_campo_de_relampagos: {
    path: "assets/icons/jutsus/campo_de_relampagos.webp",
    width: 64,
    height: 64,
  },
  technique_scroll_dominio_das_raizes: {
    path: "assets/icons/jutsus/dominio_das_raizes.webp",
    width: 64,
    height: 64,
  },
  technique_scroll_selo_demolidor: {
    path: "assets/icons/jutsus/selo_demolidor.webp",
    width: 64,
    height: 64,
  },
  technique_scroll_selo_instavel: {
    path: "assets/icons/jutsus/selo_instavel.webp",
    width: 64,
    height: 64,
  },
  technique_scroll_ruptura_das_raizes: {
    path: "assets/icons/jutsus/ruptura_das_raizes.webp",
    width: 64,
    height: 64,
  },
  technique_scroll_pulso_de_emergencia: {
    path: "assets/icons/jutsus/pulso_de_emergencia.webp",
    width: 64,
    height: 64,
  },
  technique_scroll_barreira_restauradora: {
    path: "assets/icons/jutsus/barreira_restauradora.webp",
    width: 64,
    height: 64,
  },
  technique_scroll_defesa_reflexa: {
    path: "assets/icons/jutsus/defesa_reflexa.webp",
    width: 64,
    height: 64,
  },
  technique_scroll_contra_golpe_preciso: {
    path: "assets/icons/jutsus/contra_golpe_preciso.webp",
    width: 64,
    height: 64,
  },
} as const;

/** Small type markers over technique artwork for the four scroll rarities. */
export const TECHNIQUE_SCROLL_RARITY_ICONS = {
  uncommon: {
    path: "assets/icons/scrolls/scroll-uncommon.png",
    width: 32,
    height: 32,
  },
  rare: {
    path: "assets/icons/scrolls/scroll-rare.png",
    width: 32,
    height: 32,
  },
  epic: {
    path: "assets/icons/scrolls/scroll-epic.png",
    width: 32,
    height: 32,
  },
  legendary: {
    path: "assets/icons/scrolls/scroll-legendary.png",
    width: 32,
    height: 32,
  },
} as const;

/** Curated library icons for techniques currently available in the MVP. */
export const TECHNIQUE_ICONS = {
  impacto_frontal: {
    path: "assets/icons/jutsus/impacto_frontal.webp",
    width: 64,
    height: 64,
  },
  lamina_de_vento: {
    path: "assets/icons/jutsus/lamina_de_vento.webp",
    width: 64,
    height: 64,
  },
  selo_explosivo: {
    path: "assets/icons/jutsus/selo_explosivo.webp",
    width: 64,
    height: 64,
  },
  palma_restauradora: {
    path: "assets/icons/jutsus/palma_restauradora.webp",
    width: 64,
    height: 64,
  },
  ilusao_do_medo: {
    path: "assets/icons/jutsus/ilusao_do_medo.webp",
    width: 64,
    height: 64,
  },
  queda_do_trovao: {
    path: "assets/icons/jutsus/queda_do_trovao.webp",
    width: 64,
    height: 64,
  },
  selo_de_proximidade: {
    path: "assets/icons/jutsus/selo_de_proximidade.webp",
    width: 64,
    height: 64,
  },
  campo_de_relampagos: {
    path: "assets/icons/jutsus/campo_de_relampagos.webp",
    width: 64,
    height: 64,
  },
  dominio_das_raizes: {
    path: "assets/icons/jutsus/dominio_das_raizes.webp",
    width: 64,
    height: 64,
  },
  selo_demolidor: {
    path: "assets/icons/jutsus/selo_demolidor.webp",
    width: 64,
    height: 64,
  },
  selo_instavel: {
    path: "assets/icons/jutsus/selo_instavel.webp",
    width: 64,
    height: 64,
  },
  ruptura_das_raizes: {
    path: "assets/icons/jutsus/ruptura_das_raizes.webp",
    width: 64,
    height: 64,
  },
  pulso_de_emergencia: {
    path: "assets/icons/jutsus/pulso_de_emergencia.webp",
    width: 64,
    height: 64,
  },
  barreira_restauradora: {
    path: "assets/icons/jutsus/barreira_restauradora.webp",
    width: 64,
    height: 64,
  },
  defesa_reflexa: {
    path: "assets/icons/jutsus/defesa_reflexa.webp",
    width: 64,
    height: 64,
  },
  contra_golpe_preciso: {
    path: "assets/icons/jutsus/contra_golpe_preciso.webp",
    width: 64,
    height: 64,
  },
} as const;

/** @deprecated Use ITEM_ICONS; retained for current equipment consumers. */
export const EQUIPMENT_ICONS = ITEM_ICONS;

export const BASIC_ATTACK_EFFECT_IDS = {
  miss: 4,
  hit: 5,
} as const;

export const VISUAL_EFFECTS = {
  basicAttackMiss: {
    resourceId: BASIC_ATTACK_EFFECT_IDS.miss,
    directory: "effects",
    frames: Array.from({ length: 9 }, (_, i) => i),
  },
  basicAttackHit: {
    resourceId: BASIC_ATTACK_EFFECT_IDS.hit,
    directory: "effects",
    frames: Array.from({ length: 13 }, (_, i) => i),
  },
  frontalImpact: {
    resourceId: 1464,
    directory: "effects",
    frames: Array.from({ length: 15 }, (_, i) => i),
  },
  impact: { resourceId: 37, directory: "effects", frames: [0, 1, 2, 3, 4] },
  wind: {
    resourceId: 23,
    directory: "effects",
    frames: Array.from({ length: 16 }, (_, i) => i),
  },
  windBlade: {
    resourceId: 164,
    directory: "effects",
    frames: Array.from({ length: 9 }, (_, i) => i),
  },
  restorative: {
    resourceId: 161,
    directory: "effects",
    frames: Array.from({ length: 10 }, (_, i) => i),
  },
  explosive: { resourceId: 22, directory: "effects", frames: [0, 1, 2, 3] },
  explosiveSeal: {
    resourceId: 458,
    directory: "effects",
    frames: Array.from({ length: 10 }, (_, i) => i),
  },
  explosiveDetonation: {
    resourceId: 225,
    directory: "effects",
    frames: Array.from({ length: 15 }, (_, i) => i),
  },
  fear: {
    resourceId: 836,
    directory: "effects",
    frames: Array.from({ length: 6 }, (_, i) => i),
  },
  fearStatusLoop: {
    resourceId: 2899,
    directory: "effects",
    frames: Array.from({ length: 4 }, (_, i) => i),
  },
  lightningFieldPulse: {
    resourceId: 37,
    directory: "effects",
    frames: [0, 1, 2, 3, 4],
  },
  rootsFieldPulse: {
    resourceId: 230,
    directory: "effects",
    frames: Array.from({ length: 23 }, (_, i) => i),
  },
} as const;

export interface MissileDirectionVariant {
  /** X/Y cells from the source export filename, not animation frame numbers. */
  x: 0 | 1 | 2;
  y: 0 | 1 | 2;
  /** Present only for source assets exported with an actual `_fN` frame suffix. */
  frames?: readonly number[];
}

export interface VisualMissileDefinition {
  resourceId: number;
  directions: Readonly<Record<ProjectileDirection, MissileDirectionVariant>>;
}

const eightWayDirectionVariants: Readonly<
  Record<ProjectileDirection, MissileDirectionVariant>
> = {
  north_west: { x: 0, y: 0 },
  north: { x: 1, y: 0 },
  north_east: { x: 2, y: 0 },
  west: { x: 0, y: 1 },
  east: { x: 2, y: 1 },
  south_west: { x: 0, y: 2 },
  south: { x: 1, y: 2 },
  south_east: { x: 2, y: 2 },
};

const twoFrameEightWayDirectionVariants: Readonly<
  Record<ProjectileDirection, MissileDirectionVariant>
> = {
  north_west: { ...eightWayDirectionVariants.north_west, frames: [0, 1] },
  north: { ...eightWayDirectionVariants.north, frames: [0, 1] },
  north_east: { ...eightWayDirectionVariants.north_east, frames: [0, 1] },
  west: { ...eightWayDirectionVariants.west, frames: [0, 1] },
  east: { ...eightWayDirectionVariants.east, frames: [0, 1] },
  south_west: { ...eightWayDirectionVariants.south_west, frames: [0, 1] },
  south: { ...eightWayDirectionVariants.south, frames: [0, 1] },
  south_east: { ...eightWayDirectionVariants.south_east, frames: [0, 1] },
};

// Direction cells use the verified 3x3 layout: the center is unused and the
// surrounding cells map to the eight travel directions.
export const VISUAL_MISSILES: Readonly<
  Record<string, VisualMissileDefinition>
> = {
  projectile: { resourceId: 15, directions: eightWayDirectionVariants },
  explosiveProjectile: { resourceId: 4, directions: eightWayDirectionVariants },
  explosiveTagProjectile: {
    resourceId: 173,
    directions: eightWayDirectionVariants,
  },
  windBladeProjectile: {
    resourceId: 30,
    directions: twoFrameEightWayDirectionVariants,
  },
};

export function visualMissileByResourceId(
  resourceId: number,
): VisualMissileDefinition | undefined {
  return Object.values(VISUAL_MISSILES).find(
    (missile) => missile.resourceId === resourceId,
  );
}

export function missileTextureKey(
  resourceId: number,
  direction: ProjectileDirection,
  frame?: number,
): string {
  return `missile-${resourceId}-${direction}${frame === undefined ? "" : `-f${frame}`}`;
}

export function missileAssetPath(
  missile: VisualMissileDefinition,
  direction: ProjectileDirection,
  frame?: number,
): string {
  const variant = missile.directions[direction];
  const frameSuffix = frame === undefined ? "" : `_f${frame}`;
  return `assets/missiles/missile_${missile.resourceId}_x${variant.x}_y${variant.y}${frameSuffix}.png`;
}

export function sourceOutfitFrame(
  entry: SpriteAppearance,
  frame: number,
  direction = entry.sourceDirections[0] ?? 0,
): string {
  const sourceDirection = entry.sourceDirections.includes(direction)
    ? direction
    : (entry.sourceDirections[0] ?? 0);
  const variant =
    entry.sourceGroup === null
      ? ""
      : `_g${entry.sourceGroup}t${entry.sourceType}`;
  const base = `/assets/outfits/outfit_${entry.resourceId}${variant}_x${sourceDirection}`;
  return entry.hasFrameSuffix ? `${base}_f${frame}.png` : `${base}.png`;
}
